import { FunctionComponent, memo, useEffect, useState } from "react";
import { FormProvider, useForm, SubmitHandler, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { addVideoUpload } from "../../Redux/Slices/VideoUpload/AddVideoUploadSlice";
import { editVideoUpload } from "../../Redux/Slices/VideoUpload/EditVideoUploadSlice";
import TextInputFieldC from "../../Components/TextInput/TextInputFieldC";
import TextAreaInputField from "../../Components/TextAreaInput/TextAreaInputField";
import Select from "../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";
import UploadButtonField from "../../Components/Button/UploadButtonField";
import { VideoLessonUploadForm } from "./Ivideolessons";
import { listTopicSectionVideo } from "../../Redux/Slices/VideoUpload/ListTopicSectionVideoSlice";
import { listVideoUpload } from "../../Redux/Slices/VideoUpload/ListVideoUploadSlice";
import { Checkbox, FormControlLabel } from "@mui/material";

interface VideoLessonsFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: any;
  setInitialValues: React.Dispatch<React.SetStateAction<any>>;
}

const schema = z
  .object({
    id: z.number().optional(),
    topic_id: z.number().min(1, { message: "Please select a topic" }),
    title: z
      .string()
      .min(1, { message: "Please enter video title" })
      .max(100, { message: "video title cannot exceed 100 characters" }),
    description: z
      .string()
      .min(1, { message: "Please enter description " })
      .max(500, { message: "description cannot exceed 500 characters" }),
    language: z.string().min(1, { message: "Please enter language" }),
    Status: z.boolean({
      required_error: "Please select status",
      invalid_type_error: "Please select status",
    }),
    free_tier: z.boolean().optional().default(false),
    file: z.any().optional(),
  })
  .refine(
    (data) => {
      // In ADD mode (no id), file is required
      if (!data.id) {
        return data.file && data.file instanceof File;
      }
      return true;
    },
    {
      message: "Please upload a video file",
      path: ["file"],
    }
  );

const VideoLessonsForm: FunctionComponent<VideoLessonsFormProps> = ({
  formRef,
  initialValues,
  setInitialValues,
}) => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(listVideoUpload());
    dispatch(listTopicSectionVideo());
  }, []);

  const [uploadedFileName, setUploadedFileName] = useState<string>("");

  const { data: ListTopicDetails } = useAppSelector(
    (state) => state.ListTopicSectionVideoSlice
  );
  console.log(ListTopicDetails);
  const ListTopicDetailsModified = Array.isArray(ListTopicDetails?.data)
    ? ListTopicDetails?.data?.map((listQuestions: any) => ({
      label: listQuestions?.topic_name,
      value: listQuestions.id,
    }))
    : [];
  const methods = useForm<VideoLessonUploadForm>({
    defaultValues: {
      id: initialValues?.id,
      topic_id: initialValues?.topic_id || 0,
      title: initialValues?.title ?? "",
      description: initialValues?.description ?? "",
      language: initialValues?.language ?? "",
      Status: initialValues?.id ? initialValues.Status : true,
      free_tier: initialValues?.free_tier ?? false,
      file: undefined,
    },
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      methods.reset({
        id: initialValues?.id,
        topic_id: initialValues?.topic_id || 0,
        title: initialValues?.title ?? "",
        description: initialValues?.description ?? "",
        language: initialValues?.language ?? "",
        Status: initialValues?.Status ?? true,
        free_tier: initialValues?.free_tier ?? false,
        file: undefined,
      });
    }
  }, [initialValues, methods]);

  const freeTier = methods.watch("free_tier");
  // const onSubmit: SubmitHandler<VideoLessonUploadForm> = (val) => {
  //   if (val.id) {
  //     console.log("edit");
  //     dispatch(editVideoUpload(val)); // plain object
  //   } else {
  //     console.log("add");
  //     if (val.id === undefined) {
  //       val.id = 0;
  //     }
  //     console.log("value", val);
  //     dispatch(addVideoUpload(val)); // plain object
  //   }
  // };

  const onSubmit: SubmitHandler<VideoLessonUploadForm> = (val) => {
    console.log("FORM SUBMIT VALUES 👉", val);
    // Ensure payload has defaults
    const payload: any = {
      topic_id: val.topic_id || 0,
      title: val.title || "",
      description: val.description || "",
      language: val.language || "",
      Status: val.Status !== undefined ? val.Status : true,
      free_tier: val.free_tier ?? false,
    };

    if (!val.id || (val.id && val.file)) {
      payload.file = val.file; // include file for add or if new file in edit
    }

    console.log(payload, "payloadpayload");
    if (val.id && val.id !== 0) {
      // EDIT mode: include id (no video_id/transcription/etc. sent, as they are server-generated)
      payload.id = val.id;
      console.log("edit payload:", payload);
      dispatch(editVideoUpload(payload));
    } else {
      // ADD mode: id undefined, include file
      console.log("add payload:", payload);
      dispatch(addVideoUpload(payload));
    }
  };

  useEffect(() => {
    return () => setInitialValues({});
  }, []);

  const statusOptions: AutocompleteOption[] = [
    { label: "Active", value: true },
    { label: "Inactive", value: false },
  ];
  return (
    <FormProvider {...methods}>
      <form
        ref={formRef}
        onSubmit={methods.handleSubmit(onSubmit)}
        className="form_content"
      >
        <Select
          name="topic_id" // ✅ Change from "id" to "topic_id"
          label="Topic"
          options={ListTopicDetailsModified}
          value={
            ListTopicDetailsModified?.find(
              (list: any) => list.value === methods.getValues("topic_id")
            ) ?? null
          }
          onChange={(_, value) =>
            methods.setValue("topic_id", (value as AutocompleteOption)?.value, {
              shouldValidate: true,
            })
          }
        />

        <TextInputFieldC name="title" label="Title" type="text" />
        <TextAreaInputField name="description" label="Description" />
        <TextInputFieldC name="language" label="Language" type="text" />

        <Select
          name="Status"
          label="Status"
          options={statusOptions}
          value={statusOptions.find(
            (o) => o.value === methods.getValues("Status")
          )}
          onChange={(_, value) =>
            methods.setValue("Status", (value as AutocompleteOption)?.value)
          }
        />

        <Controller
          name="free_tier"
          control={methods.control}
          defaultValue={false}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  sx={{ transform: "scale(1.3)" }}
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              }
              label="Free Tier"
              sx={{
                justifyContent: "flex-start",
                marginLeft: "0",
                gap: "8px",
              }}
            />
          )}
        />

        {!methods.getValues("id") && (
          <>
            <UploadButtonField
              name="file"
              id="file-upload"
              fileAccept="video/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  methods.setValue("file", file, { shouldValidate: true });
                  setUploadedFileName(file.name);
                }
              }}
              sx={{
                fontSize: "12px",
                color: "#ffffff",
                bgcolor: "#009DFF",
                borderRadius: "8px",
                textTransform: "none",
                borderColor: "#009DFF",
                "&:hover": {
                  opacity: 0.8,
                  bgcolor: "#009DFF",
                },
              }}
              variant="contained"
            >
              Upload Video
            </UploadButtonField>

            {uploadedFileName && (
              <div style={{ marginTop: "8px", color: "#555" }}>
                Selected File: {uploadedFileName}
              </div>
            )}
            {methods.formState.errors.file && (
              <span style={{ color: "red", fontSize: "12px", marginTop: "4px", display: "block" }}>
                {methods.formState.errors.file.message?.toString()}
              </span>
            )}
          </>
        )}
      </form>
    </FormProvider>
  );
};

export default memo(VideoLessonsForm);
